import { setAdminOrgOverride } from '@borradh-workspace/api-client';
import { QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { PlatformAccessReasonField } from '@/features/privacy/components/platform-access-reason-field';
import { makeQueryClient } from '@/lib/query-client';
import type { PlatformAccessReason } from '@borradh-workspace/labels';

/**
 * Scopes its subtree to view a single organization's data as a platform admin,
 * without swapping the admin's session.
 *
 * - Sets the api-client org-override so every request inside carries
 *   `X-Admin-Organization-Id` (honored by the backend only for verified
 *   admins). Cleared on unmount so the rest of the app is unaffected.
 * - Provides a fresh, isolated `QueryClient` keyed by `organizationId`. The
 *   inbox/campaign hooks use shared query keys (e.g. `['conversations','list']`)
 *   that don't include the org, so switching orgs would otherwise serve another
 *   org's cached data. A new client per org guarantees no cross-org bleed and
 *   leaves the main app's cache untouched.
 *
 * The override is set during render (before child queries fire) AND cleared via
 * an unmount effect — both are needed: render-time set ensures the very first
 * child request is scoped; the effect cleanup restores normal behavior on exit.
 */
interface AdminOrgScopeProps {
  organizationId: string;
  children: ReactNode;
}

export function AdminOrgScope(props: AdminOrgScopeProps) {
  return <AdminWorkspace key={props.organizationId} {...props} />;
}

function AdminWorkspace({ organizationId, children }: AdminOrgScopeProps) {
  const [reason, setReason] = useState<PlatformAccessReason>('clinic_support');
  const [opened, setOpened] = useState(false);
  const [client] = useState(makeQueryClient);

  // Set synchronously so child queries rendered this pass are already scoped.
  setAdminOrgOverride(
    opened ? organizationId : null,
    opened ? reason : undefined
  );

  useEffect(() => {
    setAdminOrgOverride(
      opened ? organizationId : null,
      opened ? reason : undefined
    );
    return () => setAdminOrgOverride(null);
  }, [opened, organizationId, reason]);

  return (
    <QueryClientProvider client={client}>
      <div className="space-y-4">
        <PlatformAccessReasonField
          id="organization-access-reason"
          value={reason}
          onChange={setReason}
        />
        {opened ? (
          children
        ) : (
          <Button onClick={() => setOpened(true)}>Open workspace</Button>
        )}
      </div>
    </QueryClientProvider>
  );
}
